import React, { useEffect, useMemo } from 'react';
import { useQuery } from '@apollo/client/react';
import { useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Tooltip, useMap } from 'react-leaflet';
import { divIcon } from 'leaflet';
import { POSITIONED_SITES_QUERY } from '../graphql/queries';
import { themeColor, useThemeMode } from './analytics/theme';
import '../styles/sites-map.css';

const TILE = {
  url: 'https://tile.openstreetmap.org/{z}/{x}/{y}.png',
  attribution: '&copy; OpenStreetMap'
};

// Fits the view to all markers once the map instance exists.
function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (points.length > 0)
      map.fitBounds(points.map((p) => [p.lat, p.lon]), { padding: [40, 40] });
  }, [map, points]);
  return null;
}

// Map of all positioned sites. Markers are clickable: clicking one opens the
// graves list with that site's filter applied (works from any page).
export default function SitesMap({ selectedId }) {
  useThemeMode();
  const navigate = useNavigate();
  const { data, loading, error } = useQuery(POSITIONED_SITES_QUERY);

  const sites = useMemo(
    () => (data?.sites || []).filter((s) => s.lat != null && s.lon != null),
    [data]
  );

  if (loading) return <div className="sites-map-placeholder">Loading map…</div>;
  if (error) return <div className="sites-map-placeholder">Map unavailable.</div>;
  if (sites.length === 0)
    return (
      <div className="sites-map-placeholder">
        No sites with coordinates yet.
      </div>
    );

  const color = themeColor('--md-sys-color-primary', '#3f51b5');
  const icon = (site) => {
    const selected = String(site.id) === String(selectedId);
    const size = selected ? 22 : 14;
    return divIcon({
      className: 'site-marker',
      html: `<div class="site-marker-dot${selected ? ' is-selected' : ''}" style="background:${color}"></div>`,
      iconSize: [size, size],
      iconAnchor: [size / 2, size / 2]
    });
  };
  const go = (site) => navigate(`/graves?siteId=${site.id}`);

  return (
    <div className="sites-map-wrap">
      <MapContainer
        center={[sites[0].lat, sites[0].lon]}
        zoom={5}
        className="sites-map"
        scrollWheelZoom
      >
        <TileLayer attribution={TILE.attribution} url={TILE.url} />
        <FitBounds points={sites} />
        {sites.map((site) => (
          <Marker
            key={site.id}
            position={[site.lat, site.lon]}
            icon={icon(site)}
            eventHandlers={{ click: () => go(site) }}
          >
            <Tooltip direction="top" offset={[0, -10]}>
              {site.name}
            </Tooltip>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
}
